import { forwardRef, useEffect, useState, type CSSProperties } from "react";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  LoaderCircle,
  ServerCog,
  TerminalSquare,
  X,
} from "lucide-react";
import type {
  AgentDefinition,
  AgentWorkspaceDefinition,
  AvailableToolDefinition,
  GitRepositoryDefinition,
  McpServerDefinition,
  McpToolDefinition,
  Model,
  ThinkingLevel,
} from "@lumen/shared";
import { t } from "../../i18n";
import { Toggle } from "../Toggle";
import { ConfigFields } from "../tools/ToolConfigFields";
import { ActivitySettingsPanel } from "./ActivitySettingsPanel";
import { SaveStatus } from "../SaveStatus";
import { McpActionPicker } from "./McpActionPicker";
import { ComposerToolPopupTools } from "./ComposerToolPopupTools";
import { activityDetails, activityLabel } from "./ComposerActivityUtils";
import type { ComposerActivity, ComposerActivitySettings } from "./Composer";

const isContainerWorkspaceType = (type: string) =>
  ["container", "process-sandbox", "rootless-podman"].includes(type);

export type ToolPopupProps = {
  section: "tools" | "environment";
  position: {
    left: number;
    bottom: number;
    width: number;
    maxHeight: number;
  };
  tools: AvailableToolDefinition[];
  toolIds: string[];
  activityModels: Record<Exclude<ComposerActivity, "ask">, Model[]>;
  activityModelIds: Partial<Record<Exclude<ComposerActivity, "ask">, string>>;
  enabledActivities: ComposerActivity[];
  onActivityModelChange: (
    activity: Exclude<ComposerActivity, "ask">,
    modelId: string,
  ) => void;
  onToggleActivity: (activity: Exclude<ComposerActivity, "ask">) => void;
  activitySettings: ComposerActivitySettings;
  activityDetail: Exclude<ComposerActivity, "ask"> | null;
  onActivitySettingsChange: (
    activity: Exclude<ComposerActivity, "ask">,
    settings: ComposerActivitySettings[Exclude<ComposerActivity, "ask">],
  ) => void;
  onOpenActivity: (activity: Exclude<ComposerActivity, "ask"> | null) => void;
  mcpServers: McpServerDefinition[];
  mcpServerIds: string[];
  mcpDetailServerId: string | null;
  mcpTools: Record<
    string,
    {
      status: "idle" | "loading" | "ready" | "error";
      tools: McpToolDefinition[];
      error?: string;
    }
  >;
  mcpToolSelections: Record<string, string[]>;
  agentWorkspaces: AgentWorkspaceDefinition[];
  agentWorkspaceIds: string[];
  agents: AgentDefinition[];
  agentId: string;
  repositories: GitRepositoryDefinition[];
  repositoryIds: Record<string, string[]>;
  supportsThinking?: boolean;
  thinkingLevel: ThinkingLevel | "";
  settingsToolId: string | null;
  saveStatus: Record<string, "saving" | "saved" | "error">;
  onToggle: (toolId: string) => void;
  onToggleMcp: (serverId: string) => void;
  onOpenMcp: (serverId: string | null) => void;
  onSetMcpTools: (
    serverId: string,
    toolNames: string[],
    checked: boolean,
  ) => void;
  onToggleAgentWorkspace: (workspaceId: string) => void;
  onClearAgentWorkspaces: () => void;
  onSelectAgent: (agentId: string) => void;
  onToggleRepository: (workspaceId: string, repositoryId: string) => void;
  onThinkingLevelChange: (level: ThinkingLevel | "") => void;
  onSetupMcp: (server: McpServerDefinition) => void;
  onOpenSettings: (toolId: string | null) => void;
  onSettingsChange: (toolId: string, settings: Record<string, unknown>) => void;
  onClose: () => void;
};

export const ToolPopup = forwardRef<HTMLDivElement, ToolPopupProps>(
  function ToolPopup(
    {
      section,
      position,
      tools,
      toolIds,
      activityModels,
      activityModelIds: _activityModelIds,
      enabledActivities,
      onActivityModelChange: _onActivityModelChange,
      onToggleActivity,
      activitySettings,
      activityDetail,
      onActivitySettingsChange,
      onOpenActivity,
      mcpServers,
      mcpServerIds,
      mcpDetailServerId,
      mcpTools,
      mcpToolSelections,
      agentWorkspaces,
      agentWorkspaceIds,
      repositories,
      repositoryIds,
      settingsToolId,
      saveStatus,
      onToggle,
      onToggleMcp,
      onOpenMcp,
      onSetMcpTools,
      onToggleAgentWorkspace,
      onClearAgentWorkspaces,
      onToggleRepository,
      onSetupMcp,
      onOpenSettings,
      onSettingsChange,
      onClose,
    },
    ref,
  ) {
    const settingsTool = settingsToolId
      ? tools.find((tool) => tool.id === settingsToolId)
      : undefined;
    const mcpDetailServer = mcpDetailServerId
      ? mcpServers.find((server) => server.id === mcpDetailServerId)
      : undefined;
    const activityDetailModels = activityDetail
      ? activityModels[activityDetail]
      : [];
    const popupStyle = {
      "--tool-popup-left": `${position.left}px`,
      "--tool-popup-bottom": `${position.bottom}px`,
      "--tool-popup-width": `${position.width}px`,
      "--tool-popup-max-height": `${position.maxHeight}px`,
    } as CSSProperties;
    const [expandedWorkspaceGroups, setExpandedWorkspaceGroups] = useState<
      Record<string, boolean>
    >({});
    useEffect(() => {
      const visibleProxmoxRoots = agentWorkspaces.filter(
        (workspace) =>
          workspace.type === "proxmox" &&
          !workspace.parentWorkspaceId &&
          agentWorkspaces.some(
            (child) => child.parentWorkspaceId === workspace.id,
          ),
      );
      setExpandedWorkspaceGroups((current) => {
        const next = { ...current };
        for (const workspace of visibleProxmoxRoots) {
          const children = agentWorkspaces.filter(
            (child) => child.parentWorkspaceId === workspace.id,
          );
          if (children.some((child) => agentWorkspaceIds.includes(child.id)))
            next[workspace.id] = true;
        }
        const machineGroups = new Map<string, AgentWorkspaceDefinition[]>();
        for (const workspace of agentWorkspaces) {
          const machineId = workspace.machine?.id;
          if (workspace.type !== "machine" || !machineId) continue;
          const group = machineGroups.get(machineId) ?? [];
          group.push(workspace);
          machineGroups.set(machineId, group);
        }
        for (const [machineId, children] of machineGroups) {
          if (children.some((child) => agentWorkspaceIds.includes(child.id)))
            next[`machine:${machineId}`] = true;
        }
        return next;
      });
    }, [agentWorkspaceIds, agentWorkspaces]);
    const workspaceChildren = new Map<string, AgentWorkspaceDefinition[]>();
    for (const workspace of agentWorkspaces) {
      if (!workspace.parentWorkspaceId) continue;
      const children = workspaceChildren.get(workspace.parentWorkspaceId) ?? [];
      children.push(workspace);
      workspaceChildren.set(workspace.parentWorkspaceId, children);
    }
    const machineWorkspaceGroups = new Map<
      string,
      AgentWorkspaceDefinition[]
    >();
    for (const workspace of agentWorkspaces) {
      const machineId = workspace.machine?.id;
      if (workspace.type !== "machine" || !machineId) continue;
      const group = machineWorkspaceGroups.get(machineId) ?? [];
      group.push(workspace);
      machineWorkspaceGroups.set(machineId, group);
    }
    const visibleWorkspaceIds = new Set(
      agentWorkspaces.map((workspace) => workspace.id),
    );
    // A child whose parent is not visible must remain selectable as a root.
    const workspaceRoots = agentWorkspaces.filter((workspace) => {
      if (workspace.type === "machine" && workspace.machine?.id) {
        const group = machineWorkspaceGroups.get(workspace.machine.id) ?? [];
        return group[0]?.id === workspace.id;
      }
      return (
        !workspace.parentWorkspaceId ||
        !visibleWorkspaceIds.has(workspace.parentWorkspaceId)
      );
    });
    const renderWorkspace = (
      workspace: AgentWorkspaceDefinition,
      nested = false,
    ) => {
      const needsSetup =
        workspace.type === "ssh" &&
        workspace.authScope === "user" &&
        (!workspace.userSshUsername || !workspace.userCredentialConfigured);
      const unavailable = workspace.available === false;
      return (
        <div
          key={workspace.id}
          className={`flex flex-wrap items-start gap-2 rounded-lg p-2 ${nested ? "ml-5 border-l border-stone-200 dark:border-white/10" : ""} ${unavailable ? "opacity-60" : ""} hover:bg-stone-50 dark:hover:bg-white/5`}
        >
          <label
            className={`flex min-w-0 flex-1 items-start gap-3 ${needsSetup || unavailable ? "cursor-not-allowed" : "cursor-pointer"}`}
          >
            <Toggle
              className="mt-1"
              disabled={needsSetup || unavailable}
              checked={agentWorkspaceIds.includes(workspace.id)}
              onChange={() => onToggleAgentWorkspace(workspace.id)}
            />
            {workspace.type === "proxmox" ? (
              <ServerCog size={15} className="mt-0.5 shrink-0 text-lumen-600" />
            ) : (
              <TerminalSquare
                size={15}
                className="mt-0.5 shrink-0 text-lumen-600"
              />
            )}
            <span className="min-w-0">
              <strong className="block text-sm">{workspace.name}</strong>
              <code className="block text-[10px] text-stone-400">
                {workspace.slug}
              </code>
              <span className="block truncate text-xs text-stone-400">
                {workspace.type === "proxmox"
                  ? workspace.proxmox?.targetKind === "host"
                    ? workspace.description
                    : `${workspace.proxmox?.node} · ${workspace.proxmox?.status ?? "unknown"}${workspace.proxmox?.executionStatus === "unavailable" ? " · unavailable" : ""}`
                  : workspace.type === "machine"
                    ? `${workspace.machine?.folderName ?? "Connected folder"} · ${workspace.machine?.platform ?? "machine"}${workspace.machine?.git?.available ? " · Git" : ""}`
                    : workspace.description ||
                      (isContainerWorkspaceType(workspace.type)
                        ? workspace.image
                        : workspace.sshHost)}
              </span>
              {workspace.workspaceMode === "machine" && (
                <span className="mt-1 block text-[10px] font-medium text-lumen-700 dark:text-lumen-300">
                  {t("Machine · shared")}
                </span>
              )}
              {unavailable && (
                <span className="mt-1 block text-xs text-amber-600 dark:text-amber-400">
                  {workspace.availabilityReason ?? "Unavailable"}
                </span>
              )}
            </span>
          </label>
          {needsSetup && (
            <button
              type="button"
              className="button shrink-0 p-1.5 text-xs"
              onClick={() => {
                window.location.href = "/settings/agent-workspaces";
              }}
            >
              {t("Set up")}
            </button>
          )}
          {agentWorkspaceIds.includes(workspace.id) &&
            isContainerWorkspaceType(workspace.type) &&
            repositories.length > 0 && (
              <div className="min-w-0 basis-full pl-8">
                <span className="mb-1 block text-[10px] font-bold tracking-wider text-stone-400 uppercase">
                  {t("Repositories")}
                </span>
                <div className="space-y-1">
                  {repositories.map((repository) => {
                    const selected = (
                      repositoryIds[workspace.id] ?? []
                    ).includes(repository.id);
                    return (
                      <label
                        key={repository.id}
                        className="flex cursor-pointer items-center gap-2 rounded p-1 text-xs hover:bg-stone-100 dark:hover:bg-white/10"
                      >
                        <Toggle
                          checked={selected}
                          onChange={() =>
                            onToggleRepository(workspace.id, repository.id)
                          }
                        />
                        <span className="min-w-0 truncate">
                          {repository.name}{" "}
                          <code className="text-[10px] text-stone-400">
                            ({repository.slug})
                          </code>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
        </div>
      );
    };
    const renderMachineGroup = (workspace: AgentWorkspaceDefinition) => {
      const machineId = workspace.machine?.id;
      const children = machineId
        ? (machineWorkspaceGroups.get(machineId) ?? [])
        : [workspace];
      const groupKey = `machine:${machineId ?? workspace.id}`;
      const expanded = expandedWorkspaceGroups[groupKey] ?? false;
      const machine = workspace.machine;
      return (
        <div key={groupKey}>
          <div className="flex items-center rounded-lg p-2 hover:bg-stone-50 dark:hover:bg-white/5">
            <button
              type="button"
              className="px-1 text-stone-400"
              onClick={() =>
                setExpandedWorkspaceGroups((current) => ({
                  ...current,
                  [groupKey]: !expanded,
                }))
              }
              aria-label={`${expanded ? "Collapse" : "Expand"} ${machine?.name ?? "connected machine"}`}
            >
              {expanded ? (
                <ChevronDown size={15} />
              ) : (
                <ChevronRight size={15} />
              )}
            </button>
            <ServerCog size={15} className="mx-2 shrink-0 text-lumen-600" />
            <span className="min-w-0 flex-1">
              <strong className="block truncate text-sm">
                {machine?.name ?? "Connected machine"}
              </strong>
              <span className="block truncate text-xs text-stone-400">
                {t("shared folder", { count: children.length })}
                {machine?.platform ? ` · ${machine.platform}` : ""}
                {machine?.status ? ` · ${machine.status}` : ""}
              </span>
            </span>
          </div>
          {expanded && children.map((child) => renderWorkspace(child, true))}
        </div>
      );
    };
    return (
      <div
        ref={ref}
        style={popupStyle}
        className={`tool-popup-responsive fixed z-[100] border border-stone-200 bg-white p-2 text-stone-900 shadow-2xl dark:border-white/10 dark:bg-[#222725] dark:text-stone-100 ${mcpDetailServer && !settingsTool && !activityDetail ? "mcp-action-popup flex flex-col overflow-hidden" : "overflow-y-auto"}`}
      >
        {settingsTool ? (
          <>
            <div className="flex items-center gap-2 border-b border-stone-100 px-1 pb-2 dark:border-white/10">
              <button
                type="button"
                className="button p-1.5"
                onClick={() => onOpenSettings(null)}
                title={t("Back to tools")}
              >
                <ArrowLeft size={16} />
              </button>
              <div className="min-w-0 flex-1">
                <strong className="block truncate text-sm">
                  {settingsTool.displayName || settingsTool.name}
                </strong>
                <span className="text-[11px] text-stone-400">
                  {t("User settings")}
                </span>
              </div>
              <SaveStatus status={saveStatus[settingsTool.id]} />
              <button
                type="button"
                className="button p-1.5 sm:hidden"
                onClick={onClose}
                aria-label={t("Close tool settings")}
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-2">
              <ConfigFields
                schema={settingsTool.userSchema}
                value={settingsTool.userSettings}
                onChange={(settings) =>
                  onSettingsChange(settingsTool.id, settings)
                }
              />
            </div>
          </>
        ) : activityDetail ? (
          <>
            <div className="flex items-center gap-2 border-b border-stone-100 px-1 pb-2 dark:border-white/10">
              <button
                type="button"
                className="button p-1.5"
                onClick={() => onOpenActivity(null)}
                title={t("Back to activities")}
              >
                <ArrowLeft size={16} />
              </button>
              {(() => {
                const Icon = activityDetails[activityDetail].icon;
                return <Icon size={16} className="text-lumen-600" />;
              })()}
              <div className="min-w-0 flex-1">
                <strong className="block truncate text-sm">
                  {activityLabel(activityDetail)}
                </strong>
                <span className="text-[11px] text-stone-400">
                  {t("Activity settings")}
                </span>
              </div>
              <button
                type="button"
                className="button p-1.5 sm:hidden"
                onClick={onClose}
                aria-label={t("Close activity settings")}
              >
                <X size={18} />
              </button>
            </div>
            <ActivitySettingsPanel
              activity={activityDetail}
              models={activityDetailModels}
              settings={activitySettings[activityDetail] ?? {}}
              allowModelSelection
              allowAgentChoice
              onChange={(settings) =>
                onActivitySettingsChange(activityDetail, settings)
              }
            />
          </>
        ) : mcpDetailServer ? (
          <>
            <div className="flex shrink-0 items-center gap-2 border-b border-stone-100 px-1 pb-2 dark:border-white/10">
              <button
                type="button"
                className="button min-h-11 min-w-11 justify-center p-1.5"
                onClick={() => onOpenMcp(null)}
                aria-label={t("Back to tools")}
              >
                <ArrowLeft size={16} />
              </button>
              <ServerCog size={16} className="text-lumen-600" />
              <div className="min-w-0 flex-1">
                <strong className="block truncate text-sm">
                  {mcpDetailServer.name}
                </strong>
                <span className="text-[11px] text-stone-400">
                  {t("MCP actions")}
                </span>
              </div>
              <button
                type="button"
                className="button min-h-11 min-w-11 justify-center p-1.5 sm:hidden"
                onClick={onClose}
                aria-label={t("Close MCP functions")}
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex min-h-0 flex-1 flex-col">
              {mcpTools[mcpDetailServer.id]?.status === "loading" ||
              !mcpTools[mcpDetailServer.id] ? (
                <p
                  role="status"
                  className="flex items-center gap-2 px-2 py-3 text-sm text-stone-500 dark:text-stone-400"
                >
                  <LoaderCircle
                    className="animate-spin"
                    size={14}
                    aria-hidden="true"
                  />
                  {t("Loading functions…")}
                </p>
              ) : mcpTools[mcpDetailServer.id].status === "error" ? (
                <p
                  role="alert"
                  className="px-2 py-3 text-sm whitespace-pre-wrap text-red-600"
                >
                  {mcpTools[mcpDetailServer.id].error}
                </p>
              ) : mcpTools[mcpDetailServer.id].tools.length === 0 ? (
                <p className="px-2 py-3 text-sm text-stone-500 dark:text-stone-400">
                  {t("This server exposes no tools.")}
                </p>
              ) : (
                <McpActionPicker
                  key={mcpDetailServer.id}
                  tools={mcpTools[mcpDetailServer.id].tools}
                  selectedNames={mcpToolSelections[mcpDetailServer.id] ?? []}
                  onSelectionChange={(names, checked) =>
                    onSetMcpTools(mcpDetailServer.id, names, checked)
                  }
                />
              )}
            </div>
          </>
        ) : (
          <>
            <div className="mb-2 flex items-center border-b border-stone-100 px-2 pb-2 dark:border-white/10">
              <strong className="text-sm">
                {section === "tools" ? "Tools" : "Environment"}
              </strong>
              <button
                type="button"
                className="button ml-auto p-1.5 sm:hidden"
                onClick={onClose}
                aria-label={t("Close panel")}
              >
                <X size={18} />
              </button>
            </div>
            <ComposerToolPopupTools
              section={section}
              activityModels={activityModels}
              activitySettings={activitySettings}
              enabledActivities={enabledActivities}
              mcpServers={mcpServers}
              mcpServerIds={mcpServerIds}
              mcpToolSelections={mcpToolSelections}
              mcpTools={mcpTools}
              onActivitySettingsChange={onActivitySettingsChange}
              onOpenActivity={onOpenActivity}
              onOpenMcp={onOpenMcp}
              onOpenSettings={onOpenSettings}
              onSetupMcp={onSetupMcp}
              onToggle={onToggle}
              onToggleActivity={onToggleActivity}
              onToggleMcp={onToggleMcp}
              toolIds={toolIds}
              tools={tools}
            />
            {section === "environment" && (
              <>
                {agentWorkspaces.length > 0 && (
                  <>
                    <button
                      type="button"
                      className="flex w-full items-start gap-3 rounded-lg p-2 text-left hover:bg-stone-50 dark:hover:bg-white/5"
                      onClick={onClearAgentWorkspaces}
                    >
                      <span className="text-sm text-stone-500">
                        {t("No agent workspace")}
                      </span>
                    </button>
                    {workspaceRoots.map((workspace) => {
                      const children =
                        workspaceChildren.get(workspace.id) ?? [];
                      const expanded =
                        expandedWorkspaceGroups[workspace.id] ?? false;
                      if (workspace.type === "machine")
                        return renderMachineGroup(workspace);
                      return (
                        <div key={workspace.id}>
                          <div className="flex items-center">
                            {workspace.type === "proxmox" &&
                              children.length > 0 && (
                                <button
                                  type="button"
                                  className="px-1 text-stone-400"
                                  onClick={() =>
                                    setExpandedWorkspaceGroups((current) => ({
                                      ...current,
                                      [workspace.id]: !expanded,
                                    }))
                                  }
                                >
                                  {expanded ? (
                                    <ChevronDown size={15} />
                                  ) : (
                                    <ChevronRight size={15} />
                                  )}
                                </button>
                              )}
                            <div className="min-w-0 flex-1">
                              {renderWorkspace(workspace)}
                            </div>
                          </div>
                          {workspace.type === "proxmox" &&
                            expanded &&
                            children.map((child) =>
                              renderWorkspace(child, true),
                            )}
                        </div>
                      );
                    })}
                  </>
                )}
              </>
            )}
            <p className="px-2 pt-2 text-[10px] leading-4 text-stone-400">
              {t(
                "Selections are attached to this message. Tool activation is remembered.",
              )}
            </p>
          </>
        )}
      </div>
    );
  },
);
